import { KeyboardEvent, MouseEvent } from 'react';
import { alpha, styled, Typography } from '@mui/material';
import { DeleteOutline, FormatListBulleted, Link as LinkIcon } from '@mui/icons-material';
import { Prompt } from 'api/model';
import ScopeIndicator from 'components/indicators/ScopeIndicator';
import usePrompt from 'api/prompts/usePrompt';
import ConfirmationDialog from 'components/dialog/ConfirmationDialog';
import useDialog from 'hooks/useDialog';
import { toast } from 'react-toastify';
import PromptUsageDialog from 'components/dialog/PromptUsageDialog';
import { ActionIconButton } from 'components/button/ActionIconButton';
import { FONT_MONO } from 'themes/tokens';

const PromptRow = styled('div')(({ theme }) => ({
  padding: theme.spacing(1.5, 1.75),
  borderRadius: '10px',
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.default,
  cursor: 'pointer',
  transition: theme.transitions.create(['border-color', 'background-color']),
  '&:hover': {
    borderColor: alpha(theme.palette.primary.main, 0.7),
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
  },
  '&:hover .prompt-row-actions, &:focus-within .prompt-row-actions': { opacity: 1 },
}));

const TypeTag = styled('span')(({ theme }) => ({
  flex: 'none',
  padding: theme.spacing(0.5, 1),
  borderRadius: '999px',
  fontSize: '0.68rem',
  fontWeight: 600,
  lineHeight: 1,
  letterSpacing: '0.02em',
  color: theme.palette.info.main,
  backgroundColor: alpha(theme.palette.primary.main, 0.22),
}));

const PreviewLine = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(1),
  fontFamily: FONT_MONO,
  fontSize: '0.72rem',
  lineHeight: 1.5,
  color: theme.palette.info.main,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

type Props = {
  prompt: Prompt;
  onSelect: () => void;
};

const PromptItem = ({ prompt, onSelect }: Props) => {
  const { remove } = usePrompt({ promptId: prompt.id, prompt });
  const [showDeleteDialog, DeleteDialog] = useDialog(ConfirmationDialog);
  const [showUsageDialog, UsageDialog] = useDialog(PromptUsageDialog);

  const isGlobal = !prompt.project_id;
  const inputs = prompt.inputs?.length ?? 0;
  const preview = prompt.description?.trim() || prompt.template?.trim();

  const stopPropagation = (fn: () => void) => (e: MouseEvent) => {
    e.stopPropagation();
    fn();
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect();
    }
  };

  const handleCopyUrl = () => {
    const url = new URL(window.location.href);
    url.searchParams.set('promptId', prompt.id);
    void navigator.clipboard.writeText(url.toString()).then(() => toast.success('Prompt link copied to clipboard'));
  };

  return (
    <PromptRow
      role="button"
      tabIndex={0}
      aria-label={`Open prompt ${prompt.prompt_name}`}
      onClick={onSelect}
      onKeyDown={handleKeyDown}
    >
      <div className="flex min-w-0 items-center gap-2.5">
        <Typography sx={{ fontWeight: 600 }} className="min-w-0 flex-1 truncate">
          {prompt.prompt_name}
        </Typography>

        {inputs > 0 && (
          <span className="hidden shrink-0 text-xs text-gray-500 dark:text-gray-400 sm:inline">
            {inputs} input{inputs !== 1 ? 's' : ''}
          </span>
        )}
        {prompt.type && <TypeTag>{prompt.type}</TypeTag>}

        <ScopeIndicator isGlobal={isGlobal} className="shrink-0" />

        <div className="prompt-row-actions flex shrink-0 gap-1 opacity-0 transition-opacity">
          <ActionIconButton
            size="small"
            color="info"
            title="View usage"
            onClick={stopPropagation(() => showUsageDialog(true))}
          >
            <FormatListBulleted fontSize="small" />
          </ActionIconButton>
          <ActionIconButton size="small" color="info" title="Copy link" onClick={stopPropagation(handleCopyUrl)}>
            <LinkIcon fontSize="small" />
          </ActionIconButton>
          <ActionIconButton
            size="small"
            color="error"
            title={prompt.is_default ? "Default prompts can't be deleted" : 'Delete'}
            disabled={prompt.is_default}
            onClick={stopPropagation(() => showDeleteDialog(true))}
          >
            <DeleteOutline fontSize="small" />
          </ActionIconButton>
        </div>
      </div>

      {preview && <PreviewLine>{preview}</PreviewLine>}

      <DeleteDialog
        title="Delete Prompt"
        onConfirm={() => remove.mutate()}
        buttonsSettings={{ confirmText: 'Delete', confirmColor: 'error' }}
      >
        <Typography>
          Are you sure you want to delete the prompt <strong>{prompt.prompt_name}</strong>? This action cannot be
          undone.
        </Typography>
      </DeleteDialog>
      <UsageDialog prompt={prompt} onClose={() => showUsageDialog(false)} />
    </PromptRow>
  );
};

export default PromptItem;
